import type { FC } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useFMContext } from '@pflegenavi/frontend/family-member-context';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import type { FormikHelpers, FormikErrors, FormikTouched } from 'formik';
import { useFormik } from 'formik';
import {
  useSetupMangopayDirectDebitValidationSchema,
  processMangopayValidationErrors,
  filterErrorsByTouched,
} from '@pflegenavi/shared/frontend';
import type {
  Country,
  SetupMangoPayDirectDebitResidentRequest,
  SetupMangoPayDirectDebitResponse,
  ValidationErrorResponse,
} from '@pflegenavi/frontend/api-nursing-home';
import { useValidateFamilyMemberRecipientData } from '@pflegenavi/frontend/api-nursing-home';
import { useSnackbar } from 'notistack';
import { MangopayBankAccountOwner } from '@pflegenavi/shared/api';
import { ClientError } from '@pflegenavi/shared-frontend/platform';
import { useInitializeFM } from '../../../hooks/useInitializeFM';
import { SetupMangopayDirectDebitView } from './SetupMangopayDirectDebitView';

interface SetupMangopayDirectDebitProps {
  countries: Country[];
  owner: MangopayBankAccountOwner | undefined;
  mandateActive: boolean;
  mandatePending: boolean;
  mandateDocumentUrl?: string;
  onCancelMandate: () => void;
  bankAccount: {
    iban: string;
    owner_name: string;
    owner_address: {
      city: string;
      address_line_1: string;
      address_line_2: string;
      region: string;
      country: string;
      postal_code: string;
    };
  };
  setupMandate: (
    params: SetupMangoPayDirectDebitResidentRequest['body']
  ) => Promise<SetupMangoPayDirectDebitResponse>;
  PaymentInitiationInfoComponent?: React.ComponentType;
  isDisabled?: boolean;
}

export interface ISetupMangopayDirectDebitValues {
  firstName: string | undefined;
  lastName: string | undefined;
  iban: string | undefined;
  city: string | undefined;
  addressLine1: string | undefined;
  addressLine2: string | undefined;
  country: string | undefined;
  postalCode: string | undefined;
}

export const SetupMangopayDirectDebit: FC<SetupMangopayDirectDebitProps> = ({
  countries,
  owner,
  bankAccount,
  mandateActive,
  mandatePending,
  mandateDocumentUrl,
  onCancelMandate,
  setupMandate,
  PaymentInitiationInfoComponent,
  isDisabled = false,
}) => {
  const { familyMember } = useFMContext();
  const { t } = useTranslation();
  const { selectedResident } = useInitializeFM();
  const { enqueueSnackbar } = useSnackbar();

  const [redirectWarningOpen, setRedirectWarningOpen] = useState(false);

  // Refs for async validation state management
  const touchedRef = useRef<FormikTouched<ISetupMangopayDirectDebitValues>>({});
  const unfilteredErrorsRef = useRef<
    FormikErrors<ISetupMangopayDirectDebitValues>
  >({});

  // Validation mutation for MangoPay recipient validation
  const { mutateAsync: validateRecipientData } =
    useValidateFamilyMemberRecipientData();

  const validationSchema = Yup.object(
    useSetupMangopayDirectDebitValidationSchema()
  );

  const prefillNames = useMemo(() => {
    if (owner === MangopayBankAccountOwner.FamilyMember) {
      return {
        firstName: familyMember?.given_name,
        lastName: familyMember?.family_name,
      };
    } else if (owner === MangopayBankAccountOwner.Resident) {
      return {
        firstName: selectedResident?.firstName,
        lastName: selectedResident?.lastName,
      };
    } else {
      return { firstName: undefined, lastName: undefined };
    }
  }, [selectedResident, familyMember, owner]);

  // The user needs to select the owner first.
  const disabled = owner === undefined || mandateActive || isDisabled;

  const initialValues: ISetupMangopayDirectDebitValues = useMemo(() => {
    // Split owner_name if it exists (backward compatibility)
    const nameParts = bankAccount.owner_name?.split(' ') || [];
    const existingFirstName = nameParts[0] || undefined;
    const existingLastName = nameParts.slice(1).join(' ') || undefined;

    return owner !== undefined
      ? {
          firstName: existingFirstName || prefillNames.firstName,
          lastName: existingLastName || prefillNames.lastName,
          iban: bankAccount.iban,
          city: bankAccount.owner_address.city,
          addressLine1: bankAccount.owner_address.address_line_1,
          addressLine2: bankAccount.owner_address.address_line_2,
          country: bankAccount.owner_address.country,
          postalCode: bankAccount.owner_address.postal_code,
        }
      : {
          firstName: undefined,
          lastName: undefined,
          iban: undefined,
          city: undefined,
          addressLine1: undefined,
          addressLine2: undefined,
          country: undefined,
          postalCode: undefined,
        };
  }, [
    bankAccount.owner_name,
    prefillNames.firstName,
    prefillNames.lastName,
    bankAccount.iban,
    bankAccount.owner_address.city,
    bankAccount.owner_address.address_line_1,
    bankAccount.owner_address.address_line_2,
    bankAccount.owner_address.country,
    bankAccount.owner_address.postal_code,
    owner,
  ]);

  // Async validation function that calls MangoPay validation API
  const validateAsync = useCallback(
    async (
      values: ISetupMangopayDirectDebitValues
    ): Promise<FormikErrors<ISetupMangopayDirectDebitValues>> => {
      // Skip validation if we don't have the required context
      if (
        !selectedResident?.tenant ||
        !selectedResident?.residentId ||
        !selectedResident?.tenantPaymentProcessorMode
      ) {
        return {};
      }

      // Skip validation if no meaningful data to validate
      // We need at least IBAN or some address data to make an API call worthwhile
      const hasIban = !!values.iban?.trim();
      const hasAddressData =
        !!values.city?.trim() ||
        !!values.addressLine1?.trim() ||
        !!values.postalCode?.trim();

      if (!hasIban && !hasAddressData) {
        return {};
      }

      // Run sync validation first - sync errors take precedence over async API errors
      // This ensures specific errors like "Address cannot contain '/'" are shown
      // instead of generic API errors like "Invalid format"
      try {
        await validationSchema.validate(values, { abortEarly: false });
      } catch (yupError) {
        if (yupError instanceof Yup.ValidationError) {
          const syncErrors: FormikErrors<ISetupMangopayDirectDebitValues> = {};
          yupError.inner.forEach((err) => {
            if (err.path) {
              syncErrors[err.path as keyof ISetupMangopayDirectDebitValues] =
                err.message;
            }
          });
          // Return sync errors - don't call API if sync validation fails
          return syncErrors;
        }
      }

      try {
        // Transform form values to MangoPay recipient format
        // Only include fields that have values
        const recipientData: Record<string, unknown> = {};

        if (values.firstName?.trim()) {
          recipientData.FirstName = values.firstName;
        }
        if (values.lastName?.trim()) {
          recipientData.LastName = values.lastName;
        }
        if (values.iban?.trim()) {
          recipientData.IBAN = values.iban;
        }

        // Build address only if we have any address fields
        const address: Record<string, string> = {};
        if (values.addressLine1?.trim()) {
          address.AddressLine1 = values.addressLine1;
        }
        if (values.addressLine2?.trim()) {
          address.AddressLine2 = values.addressLine2;
        }
        if (values.city?.trim()) {
          address.City = values.city;
        }
        if (values.postalCode?.trim()) {
          address.PostalCode = values.postalCode;
        }
        if (values.country?.trim()) {
          address.Country = values.country;
        }

        if (Object.keys(address).length > 0) {
          recipientData.Address = address;
        }

        await validateRecipientData({
          params: {
            paymentProcessorMode: selectedResident.tenantPaymentProcessorMode,
            tenant: selectedResident.tenant,
            resident_id: selectedResident.residentId,
          },
          body: {
            currency: 'EUR',
            country: values.country,
            payout_method_type: 'LocalBankTransfer',
            recipient_type: 'INDIVIDUAL',
            recipient_data: recipientData,
          },
        });

        // Validation passed - clear errors
        unfilteredErrorsRef.current = {};
        return {};
      } catch (error: unknown) {
        // Handle validation errors from the API
        if (error instanceof ClientError) {
          const validationError = error as ValidationErrorResponse &
            ClientError;

          if (validationError.errors && Array.isArray(validationError.errors)) {
            // Translate error messages
            const translatedErrors = validationError.errors.map((err) => {
              const translationKey = `mangopay.recipient.errors.${err.message}`;
              const translatedMessage = t(translationKey, '');

              return {
                field: err.field,
                message: translatedMessage || err.message,
              };
            });

            // Transform to Formik format and store unfiltered errors
            const nestedErrors =
              processMangopayValidationErrors(translatedErrors);
            unfilteredErrorsRef.current =
              nestedErrors as FormikErrors<ISetupMangopayDirectDebitValues>;

            // Filter errors to only show for touched fields
            const filteredErrors = filterErrorsByTouched(
              nestedErrors,
              touchedRef.current
            );

            return filteredErrors as FormikErrors<ISetupMangopayDirectDebitValues>;
          }
        }

        // Return empty errors for unexpected error types
        return {};
      }
    },
    [selectedResident, validateRecipientData, t, validationSchema]
  );

  const handleSubmit = useCallback(
    async (
      values: ISetupMangopayDirectDebitValues,
      actions: FormikHelpers<ISetupMangopayDirectDebitValues>
    ) => {
      if (
        !values.firstName ||
        !values.lastName ||
        !values.iban ||
        !values.city ||
        !values.addressLine1 ||
        !values.country ||
        !values.postalCode
      ) {
        throw new Error('Form is not valid');
      }
      actions.setSubmitting(true);

      try {
        const { data } = await setupMandate({
          bank_account: {
            iban: values.iban,
            first_name: values.firstName,
            last_name: values.lastName,
            owner_address: {
              city: values.city,
              address_line_1: values.addressLine1,
              // Only include address_line_2 if it has a value (MangoPay rejects empty strings)
              ...(values.addressLine2
                ? { address_line_2: values.addressLine2 }
                : {}),
              country: values.country,
              postal_code: values.postalCode,
            },
          },
        });

        if (data.mandate.document_url) {
          window.location.href = data.mandate.document_url;
        }
        actions.resetForm({ values });
      } catch (error: unknown) {
        // Check if this is a MangoPay validation error with field-specific errors
        const validationError = error as ValidationErrorResponse | undefined;
        if (validationError?.errors && Array.isArray(validationError.errors)) {
          // Transform MangoPay errors to Formik errors and display them
          const formikErrors = processMangopayValidationErrors(
            validationError.errors.map((err) => ({
              field: err.field,
              message:
                t(`mangopay.recipient.errors.${err.message}`, '') ||
                err.message,
            }))
          );
          actions.setErrors(formikErrors);

          // Show first error in snackbar
          const firstError = validationError.errors[0];
          if (firstError) {
            const translatedMessage =
              t(`mangopay.recipient.errors.${firstError.message}`, '') ||
              firstError.message;
            enqueueSnackbar(translatedMessage, { variant: 'error' });
          }
        } else {
          enqueueSnackbar(t('errors.something-went-wrong'), {
            variant: 'error',
          });
        }
      } finally {
        actions.setSubmitting(false);
      }
    },
    [setupMandate, t, enqueueSnackbar]
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema,
    validate: validateAsync,
    validateOnChange: false, // Only validate on blur to avoid excessive API calls
    validateOnBlur: true,
    validateOnMount: false,
  });

  // Keep touched ref in sync with formik touched state
  // This is needed for filtering validation errors
  if (formik.touched !== touchedRef.current) {
    touchedRef.current = formik.touched;
  }

  const countryOptions = useMemo(() => {
    return countries.map((country) => ({
      label: t(`countries.${country.iso_code}`),
      value: country.iso_code,
    }));
  }, [countries, t]);

  const onChangeCountry = useCallback(
    (newCountryId: string) => {
      if (formik.values.country === newCountryId) {
        return;
      }

      formik.setValues((values) => ({
        ...values,
        country: newCountryId,
      }));
      formik.setFieldError('country', undefined);
    },
    [formik]
  );

  const onCountryBlur = useCallback(() => {
    formik.setFieldTouched('country', true);
  }, [formik]);

  // Handler to touch all fields and validate before submission
  // This ensures validation errors are shown for all fields, not just touched ones
  const onInitiateSubmit = useCallback(async () => {
    const allTouched = {
      firstName: true,
      lastName: true,
      iban: true,
      city: true,
      addressLine1: true,
      addressLine2: true,
      country: true,
      postalCode: true,
    };

    // Update the ref immediately so validation sees all fields as touched
    touchedRef.current = allTouched;

    // Update formik's touched state
    await formik.setTouched(allTouched);

    // Validate the form
    const errors = await formik.validateForm();

    // Return true if form is valid (no errors)
    return Object.keys(errors).length === 0;
  }, [formik]);

  return (
    <SetupMangopayDirectDebitView
      formik={formik}
      disabled={disabled}
      mandatePending={mandatePending}
      mandateDocumentUrl={mandateDocumentUrl}
      onCancelMandate={onCancelMandate}
      countryOptions={countryOptions}
      onChangeCountry={onChangeCountry}
      onCountryBlur={onCountryBlur}
      onInitiateSubmit={onInitiateSubmit}
      redirectWarningOpen={redirectWarningOpen}
      setRedirectWarningOpen={setRedirectWarningOpen}
      PaymentInitiationInfoComponent={PaymentInitiationInfoComponent}
    />
  );
};
